import type { CaseStudy } from '../../types/content'

export const bhoomiCoffee: CaseStudy = {
  slug: 'bhoomi-coffee',
  summary:
    'A self-initiated concept. We invented a ten-year-old speciality roaster with a serious wholesale reputation and an online shop that behaves like a supermarket shelf, then rebuilt the storefront around provenance, roast character and repeat ordering.',
  overview: {
    industry: 'Concept · Speciality coffee retail',
    location: 'Concept · Bengaluru, Karnataka',
    timeline: 'Self-initiated concept, 2026',
    services: ['Brand direction', 'E-commerce design', 'Shopify design system', 'Prototype'],
  },
  problem: {
    heading: 'The problem',
    paragraphs: [
      'This one is ours. No client commissioned it, nobody paid for it, and Bhoomi Coffee Roasters does not exist. We publish it because the problem it addresses is one we meet in almost every speciality retail brief, and because a concept lets us show the reasoning without redacting half of it.',
      'The premise: a roaster supplying eleven cafés across Bengaluru, whose name carries real weight in the trade — buyers know the sourcing, they know the consistency — and none of that reaches the website. The shop is assembled from a marketplace theme and patched every quarter. It opens on a carousel of discount codes, sorts the catalogue by price, and describes every coffee with the same three adjectives. A first-time visitor has no way to tell a competition lot from Chikmagalur apart from the house espresso except by the number underneath it.',
      'The commercial consequence is the interesting part, and it is the pattern we see repeatedly rather than a measured figure: a price-sorted catalogue teaches customers that price is the only comparable attribute, so average order value drifts down, the discount code becomes the default rather than the exception, and subscriptions — the healthiest revenue in coffee — never get a designed path. In the scenario we set, average order value has slid from ₹ 1,240 to ₹ 890 and subscriptions account for under a tenth of sales.',
    ],
  },
  research: {
    heading: 'Assumptions and discovery',
    paragraphs: [
      'Because this is concept work, there is no client data behind it. What follows are the assumptions we built the design against, drawn from patterns in speciality retail rather than from interviews we did not run. We state them plainly so the design can be judged against them.',
      'We assumed a template-heavy storefront where most pages have not been touched since launch, and modelled an audit of 34 templates scored for purpose, traffic and last edit. Roughly half of any such catalogue is usually dead weight, which is what justifies restructuring rather than restyling.',
      'We assumed the reorder path carries the revenue and is the least designed surface on the site — that returning customers do not browse at all, but search from memory and leave when the spelling does not match. Recognition beats recall, and that assumption is what the buy-it-again rail exists to test.',
      'We assumed two audiences with opposite needs: regulars who can describe coffee by estate and process, and first-timers who filter by price because nothing else on the page is comparable. And we assumed payment friction at the last step, with the theme pushing cards ahead of UPI. Each of these is a hypothesis a real engagement would validate in week one; here they are the brief.',
    ],
  },
  artifacts: [
    {
      kind: 'audit',
      title: 'Template and content audit',
      caption: 'Modelled: 34 templates scored for purpose, traffic and last edit',
    },
    {
      kind: 'flow',
      title: 'Reorder journey map',
      caption: 'Where we expect returning customers to drop out, step by step',
    },
    {
      kind: 'sitemap',
      title: 'Proposed catalogue structure',
      caption: 'Roast-led navigation replacing a flat price-sorted grid',
    },
  ],
  strategy: {
    heading: 'Strategy and decisions',
    paragraphs: [
      'The strategy came down to one sentence we wrote early and never revised: sell the roast, not the discount. Every decision after that had to survive being tested against it.',
      'That meant giving provenance the space the price used to occupy, restructuring the catalogue around how coffee people actually choose — roast character first, estate second, format third — and making the reorder path a designed surface rather than a search box. It also meant removing things: the sitewide discount banner, the price sort, and the review widget nobody uses.',
      'We were deliberate about what we did not do. Bhoomi did not need a new logo, a rebrand, or a bigger catalogue. The packaging in the concept is already excellent, so the site borrows its language instead of inventing a second one.',
    ],
  },
  decisions: [
    {
      decision: 'Killed the sitewide discount banner',
      reasoning:
        'It trains every visitor to wait for a code. Removing it should cost a small number of first orders and return them at full margin — a trade we would want measured, not assumed.',
    },
    {
      decision: 'Replaced price sort with roast character',
      reasoning:
        'Price sorting frames coffee as a commodity. Sorting by roast profile lets a beginner and a competition buyer both find their shelf.',
    },
    {
      decision: 'Gave subscriptions their own designed path',
      reasoning:
        'Subscription is usually a checkbox at checkout. Here it becomes a decision with its own page, cadence choice and pause control, because retention is the whole business.',
    },
    {
      decision: 'Put UPI first at checkout, cards second',
      reasoning:
        'Inherited themes almost always default to a card-first checkout. Reordering the payment options to match how customers actually pay costs nothing upstream and removes the most common last-step hesitation.',
    },
  ],
  design: {
    heading: 'Design direction',
    paragraphs: [
      'The visual direction takes its cue from the bags: a warm off-white paper ground, a single deep espresso ink for type, and one clay accent used for nothing except the buy action. Photography is shot on the same paper stock, so product images and page background share a surface.',
      'Typography carries the hierarchy. A high-contrast serif sets the coffee names at editorial scale, which lets a product page open with an estate rather than a price. Everything functional — weights, grinds, prices, delivery copy — sits in one neutral sans at two sizes, so the interface never competes with the catalogue.',
      'The component set is intentionally small: a product card with room for tasting notes, a roast-character scale drawn as a five-step rule, a subscription cadence selector, and a sticky basket summary. Because the system is small, a fulfilment team could add a new lot in four minutes without asking a designer anything.',
    ],
  },
  typefaces: [
    { name: 'Instrument Serif', role: 'Coffee names and editorial headlines' },
    { name: 'Inter Tight', role: 'Interface, specifications and body copy' },
  ],
  palette: [
    { name: 'Paper', hex: '#F6F2EA' },
    { name: 'Espresso', hex: '#241A14' },
    { name: 'Clay', hex: '#B4633C' },
    { name: 'Hairline', hex: '#E4DCCE' },
  ],
  components: [
    'Product card with tasting notes',
    'Roast character scale',
    'Subscription cadence selector',
    'Buy-it-again rail',
    'Sticky basket summary',
    'Wholesale enquiry form',
  ],
  mockupCaption: 'Storefront, product page and subscription flow, desktop and mobile',
  before: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/43d1059c-7033-425c-865b-540530b50a63.jpg',
    alt: 'The starting point in the concept: a cluttered layout with discount banners and a price-sorted product grid',
  },
  results: {
    isProjected: true,
    intro:
      'Nothing here has been measured in market. These are the outcomes the design was built to hit, stated as targets so they can be argued with.',
    metrics: [
      { value: 'Full margin', label: 'Target: orders placed without a discount code' },
      { value: '₹ 1,200+', label: 'Target average order value, up from ₹ 890' },
      { value: 'Subscription', label: 'Target: the default repeat path, not a checkbox' },
      { value: '4 min', label: 'Target time for staff to publish a new lot' },
    ],
    note:
      'We would judge this successful if the same coffee sold at full margin to more people, and if the subscription base covered the roasting schedule two weeks ahead. That is the goal the design was built for, not a result we can claim.',
  },
}
